/**
 * BRAND LINKS — the single source of destinations for the header and the footer.
 *
 * The STRUCTURE (menu groups, labels, one-line descriptions) mirrors the V3
 * design's nav and footer, via `app/src/content/navigation.ts`,
 * so the bar and footer do not change shape when a visitor crosses from the
 * marketing site into /pub. One deliberate difference: marketing routes are
 * absolute (`SITE` + path). The public site serves them from its own root; this
 * app is mounted under basePath `/pub`, so a root-relative "/platform" would
 * resolve to nothing here.
 *
 * ── DESTINATIONS ───────────────────────────────────────────────────────────
 * One path per entry, the V3 route, on every environment.
 *
 * Until 2026-09-02 each entry named TWO paths — `site(live, v3)` — because the
 * redesign SPA had not replaced Webflow on www.hrmless.com, so the same menu
 * item needed /how-nerva-works on .com and /platform on .dev, and a
 * USE_V3_ROUTES flag chose between them per build. The redesign is now live on
 * both, the Webflow paths are retired, and the flag has gone with them.
 *
 * That also settles the warning this comment used to carry: Competencies,
 * Industries Overview, Airports and Warehousing had no Webflow equivalent and
 * 404'd on a .com build. Every path below now resolves — checked against the
 * app's own router (app/src/routes.tsx), which is the authority: /platform is
 * an IN_PAGE_ANCHOR, the six verticals are industriesV3 slugs, /terms and
 * /privacy are legalSlugs, and the rest are named routes.
 */

/**
 * ── BUILD-TIME ENVIRONMENT ──────────────────────────────────────────────────
 * These five values are per-environment, so the app links to the environment it
 * is deployed alongside instead of always crossing to prod. They are resolved
 * from the build's VITE_* vars into config/buildEnv.ts by
 * scripts/write-build-env.mjs and imported here as ordinary module constants —
 * see that script for why import.meta.env is not read directly.
 *
 *   SITE                    marketing site the header/footer link out to. Still
 *                           per-environment: a .dev build links to .dev. Only
 *                           the PATHS stopped varying, not the origin.
 *   SIGN_IN_URL             the "Sign In" pill → the product app.
 *   TRUST_CENTER_URL        Trust Center, in the nav and the footer.
 *   KEYCLOAK_ISSUER         realm this app authenticates against. Consumed by
 *                           zudoku.config.tsx; the dev cluster does not serve
 *                           login.hrmless.com at all.
 *   KOMBINATION_OPENAPI_URL live spec for the /api playground. Consumed by
 *                           config/api.tsx.
 *
 * deploy.sh does `source .env`, so the vars must be `export`ed there to reach
 * the build. See .env.example.
 *
 * Imported (local bindings, so the nav tables below can use them) AND
 * re-exported (so zudoku.config.tsx and config/api.tsx keep importing from
 * brand.ts as their single source). `export ... from` alone would re-export
 * without binding locally, which breaks TRUST_CENTER_URL in navDropdowns.
 */
import {
  SITE,
  SIGN_IN_URL,
  TRUST_CENTER_URL,
  KEYCLOAK_ISSUER,
  KOMBINATION_OPENAPI_URL,
} from "../config/buildEnv";

export { SITE, SIGN_IN_URL, TRUST_CENTER_URL, KEYCLOAK_ISSUER, KOMBINATION_OPENAPI_URL };

/** This app, mounted under basePath /pub. */
export const DOCS_BASE = "/pub";

/**
 * Fallback social-card image, for pages with no image of their own, and the
 * Organization logo in the Article JSON-LD. See components/SeoHead.tsx.
 *
 * PNG on purpose. X, LinkedIn and Slack all decline to render an SVG og:image,
 * and Google's Article rich-result validator rejects SVG for Organization.logo
 * — so hrmless-logo.svg, which is right for the header, is wrong for both of
 * these and fails silently in each case (a card with no picture, a warning in
 * Search Console). Path is app-root-relative; it already carries /pub, matching
 * the images sync_content.py writes into config/blogPosts.json.
 */
export const BRAND_IMAGE = `${DOCS_BASE}/imgs/hrmless-website-logo.png`;

/**
 * Atom feed, written by scripts/generate-feed.mjs. Named here so the
 * autodiscovery <link> in SeoHead/BlogHome and the path the script writes to
 * cannot drift apart. Atom rather than RSS 2.0 — see that script's header.
 */
export const FEED_PATH = `${DOCS_BASE}/feed.xml`;


/** A marketing-site path as an absolute URL. See "DESTINATIONS" above. */
const site = (path: string) => `${SITE}${path}`;

export const DEMO_URL = site("/demo");
/** The footer CTA band's second button, "Explore platform", as on the marketing site. */
export const PLATFORM_URL = site("/platform");

export interface NavItem {
  readonly name: string;
  readonly desc: string;
  readonly href: string;
  /** Set for off-site destinations so components add target/rel. */
  readonly external?: boolean;
}

export interface NavLink {
  readonly name: string;
  readonly href: string;
  readonly external?: boolean;
}

/** RPO — a plain link in the header (between Industries and Resources), as on
 *  the marketing site's nav since 2026-10-26. */
export const RPO_LINK: NavLink = { name: "RPO", href: site("/rpo"), external: true };

/* ── Header dropdowns ──────────────────────────────────────────────────── */

export const navDropdowns: Record<"platform" | "industries" | "resources", readonly NavItem[]> = {
  platform: [
    { name: "Platform overview", desc: "Everything HRMLESS does, on one page.", href: site("/platform"), external: true },
    { name: "AI Interviews", desc: "Instant, automated 24/7 screenings", href: site("/ai-interviewer"), external: true },
    { name: "Competencies", desc: "Score every candidate against your top performers", href: site("/competencies"), external: true },
    { name: "Integrations", desc: "Sync seamlessly with your existing ATS/HRIS", href: site("/integrations"), external: true },
  ],
  industries: [
    { name: "Industries Overview", desc: "Every high volume industry HRMLESS hires for.", href: site("/industries"), external: true },
    { name: "Logistics", desc: "Keep trucks moving while AI works the phones.", href: site("/logistics"), external: true },
    { name: "Hospitality", desc: "Hire for service at any volume.", href: site("/hospitality"), external: true },
    { name: "Staffing", desc: "Submit qualified candidates before your competitors call back.", href: site("/staffing"), external: true },
    { name: "Airports", desc: "Staff every terminal, every shift, every season.", href: site("/airports"), external: true },
    { name: "QSR", desc: "Hire crew before the lunch rush.", href: site("/qsr"), external: true },
    { name: "Warehousing", desc: "From application to the floor in days, not weeks.", href: site("/warehousing"), external: true },
  ],
  // Matches the public site's Resources dropdown exactly, names, descriptions and
  // order (app/src/content/navigation.ts navDropdowns.resources). Documentation is
  // deliberately absent from both (Ben Schuster, 2026-10-26: the docs are not
  // ready); /pub/docs/home and /pub/api are still served, only unlinked here.
  resources: [
    { name: "Compare", desc: "How HRMLESS compares to other AI interview platforms", href: site("/compare"), external: true },
    { name: "FAQ", desc: "How it works, security, and what candidates can expect", href: site("/faq"), external: true },
    { name: "Blog", desc: "Latest insights on recruitment automation", href: `${DOCS_BASE}/` },
    { name: "Trust Center", desc: "Security, compliance, and SOC 2 data safety", href: TRUST_CENTER_URL, external: true },
  ],
};

/* ── Footer columns ────────────────────────────────────────────────────── */

export const footerLinks: Record<"product" | "industries" | "resources" | "company", readonly NavLink[]> = {
  product: [
    { name: "Platform", href: site("/platform"), external: true },
    { name: "AI Interviews", href: site("/ai-interviewer"), external: true },
    { name: "Competencies", href: site("/competencies"), external: true },
    { name: "Integrations", href: site("/integrations"), external: true },
    RPO_LINK,
  ],
  industries: [
    { name: "Logistics", href: site("/logistics"), external: true },
    { name: "Hospitality", href: site("/hospitality"), external: true },
    { name: "Staffing", href: site("/staffing"), external: true },
    { name: "Airports", href: site("/airports"), external: true },
    { name: "QSR", href: site("/qsr"), external: true },
    { name: "Warehousing", href: site("/warehousing"), external: true },
  ],
  // Mirrors the public site's footer column for column
  // (app/src/content/navigation.ts footerLinks) so the
  // footer does not change shape when a visitor crosses into /pub.
  // "Customers" and "Customer stories" removed with the public footer's
  // (Ben Schuster, 2026-10-26); Documentation is absent from both.
  resources: [
    { name: "Compare", href: site("/compare"), external: true },
    { name: "FAQ", href: site("/faq"), external: true },
    { name: "Blog", href: `${DOCS_BASE}/` },
    { name: "Trust Center", href: TRUST_CENTER_URL, external: true },
  ],
  // Pricing and Contact were extra entries here that the public footer does not
  // carry; "About" was missing until 2026-10-26 (Ben Schuster: match the app's
  // footer exactly). Every column now matches it link for link.
  // (The header's "Platform overview" and "Industries Overview" above are NOT
  // drift: Navigation.tsx writes both into its desktop menus by hand, outside
  // navDropdowns.)
  company: [
    { name: "About", href: site("/about"), external: true },
    { name: "Terms of Service", href: site("/terms"), external: true },
    { name: "Privacy Notice", href: site("/privacy"), external: true },
  ],
};

export const LEGAL_LINKS = {
  privacy: site("/privacy"),
  terms: site("/terms"),
} as const;

/** Tagline, per Ben Schuster's design-canvas comment (2026-08-25). */
export const TAGLINE = "Less HRM. More Productivity.";

/**
 * ── PAGE TITLE BUDGET ───────────────────────────────────────────────────────
 * The <title> template zudoku hands to unhead as `titleTemplate`, and the length
 * ceiling a page title has to fit inside.
 *
 * TITLE_TEMPLATE is imported by zudoku.config.tsx as `metadata.title`, so this
 * file is the one place the suffix is written. seoTitle() below measures the
 * suffix off the template rather than assuming " | HRMLESS" is ten characters —
 * change the template and the truncation budget follows it.
 *
 * 70 is the threshold the SEO crawlers warn at (Google truncates the SERP title
 * around there and, past it, may substitute its own). The 104 blog posts that
 * were over it were over it BECAUSE of the suffix: the longest frontmatter title
 * is 97 characters and the rendered title was 107.
 */
export const TITLE_TEMPLATE = "%s | HRMLESS";
export const TITLE_MAX = 70;

/**
 * Shorten a page title so `TITLE_TEMPLATE` applied to it lands within TITLE_MAX.
 *
 * Returns the title UNCHANGED whenever it already fits — a naive truncate adds
 * characters to a title just under the line (66 + "..." = 69) rather than saving
 * any. It only ever shortens.
 *
 * Cuts at a word boundary and strips trailing punctuation before appending the
 * ellipsis, so the result reads "…Revolutionize Recruiting..." and never
 * "…Recruiting:..." or "…Recruiting ...".
 *
 * ⚠️ This is for the <title> ONLY. og:title, twitter:title, the Article
 * headline, the Atom feed and the visible <h1> all keep the full title on
 * purpose: none of them is a SERP title, none has a 70-character budget, and a
 * social card renders the long form fine. See components/SeoHead.tsx.
 */
export const seoTitle = (title: string): string => {
  const suffix = TITLE_TEMPLATE.replace("%s", "").length;
  const budget = TITLE_MAX - suffix;
  if (title.length <= budget) return title;

  const ellipsis = "...";
  const cut = title.slice(0, budget - ellipsis.length);
  // Drop the partial word the slice ended in — unless the slice happened to land
  // on a space (no partial word to drop, and lastIndexOf would eat a whole good
  // one) or the title has no spaces at all inside the budget (lastIndexOf -1).
  const lastSpace = cut.lastIndexOf(" ");
  const atBoundary = /\s/.test(title[cut.length] ?? "");
  const words = atBoundary || lastSpace === -1 ? cut : cut.slice(0, lastSpace);
  return `${words.replace(/[\s\p{P}]+$/u, "")}${ellipsis}`;
};
